import EditorJS from "@editorjs/editorjs";

// Define an object mapping block types to their corresponding dynamic import functions
const blocksUsed = {
    header: () => import("@editorjs/header"),
    delimiter: () => import("@editorjs/delimiter"),
    link: () => import("@editorjs/link"),
    RawTool: () => import("@editorjs/raw"),
    quote: () => import("@editorjs/quote"),
    table: () => import("@editorjs/table"),
    checklist: () => import("@editorjs/checklist"),
    image: () => import("./editorjs/GalleryImage"),
    nestedList: () => import("@editorjs/nested-list"),
    list: () => import("./editorjs/CustomEditorList.js"),
    unorderedList: () => import("@editorjs/list"),
    embed: () => import("@editorjs/embed"),
    alert: () => import("editorjs-alert"),
    iframe: () => import("@hammaadhrasheedh/editorjs-iframe"),
    marker: () => import("@editorjs/marker"),
    reusableBlock: () => import("./editorjs/ReusableBlock"),
};

// Getting tools in formatted way
function getModuleValues(module, blockName) {
    let values = null;
    switch (blockName) {
    case "table":
        values = {
            class: module.default || module,
            inlineToolbar: true,
        };
        break;

    case "image":
        values = {
            class: module.default || module,
            config: { readOnly: true },
        };
        break;

    case "marker":
        values = {
            class: module.default || module,
            shortcut: "CMD+SHIFT+M",
        };
        break;

    case "list":
        values = {
            class: module.default || module,
            inlineToolbar: true,
            config: {
                defaultStyle: "unordered",
            },
        };
        break;

    default:
        values = {
            class: module.default || module,
        };
        break;
    }
    return values;
}

// Function to fetch and configure tools dynamically based on the block names used in the editor
async function getBlockConfig(blockNames) {
    const result = {};
    for (const blockName of blockNames) {
        if (blocksUsed[blockName]) {
            // Import the componnets dynamically
            const module = await blocksUsed[blockName]();
            result[blockName] = getModuleValues(module, blockName);
        }
    }
    return result;
}

// Function to initialize an individual EditorJS instance
async function initializeEditor(editorId, blocksId) {
    const data = JSON.parse(
        document.getElementById(blocksId).value.replace("&quot;", "") || "{}",
    );

    // Get the different block types used in the overall editor
    const blockTypes = data?.blocks.map((block) => block.type);
    // Get only unique block types from used components
    const uniqueBlockTypes = [...new Set(blockTypes)];

    // Get the used tools dynamically in required format
    const tools = await getBlockConfig(uniqueBlockTypes);

    return new EditorJS({
        holder: editorId,
        readOnly: true,
        data: data,
        tools: tools,
    });
}

// Function to initialize multiple EditorJS instances for a set of editors
async function initializeEditors(editors) {
    const editorInstances = {};

    for (const [index, editor] of editors.entries()) {
        if (document.getElementById(editor.editorId) != null) {
            editorInstances[`editor${index + 1}`] = await initializeEditor(
                editor.editorId,
                editor.blocksId,
            );
        }
    }

    return editorInstances;
}

// Add an event listener to initialize editors when the DOM content is fully loaded
window.addEventListener("DOMContentLoaded", async () => {
    // Fetch the list of editors from a global `window.editors` variable
    const editors = window.editors || [];

    // Initialize all editors and store their instances in a global `window.editorInstances` variable
    window.editorInstances = await initializeEditors(editors);
});
